import { Deserializer } from "jsonapi-serializer";
import { ROLES } from "../constants/preset-constants";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { Permission } from "../models/Permission";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { PermissionWrapper } from "../models/wrappers/PermissionWrapper";
import { APIConnector } from './api/APIConnector';
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { Translator } from "../contexts/translator";
import { getPermissionTypes } from "../constants/permission-constant";
const apiConnector = new APIConnector();


export const getPermissionNames = async (listParameters?: ListQuery, profileId?: number, loggedInUserProfileId?: number,): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `roleProfiles?include=site&fields[roleProfiles]=trnName,site&fields[site]=siteName${partialURL}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, async (error: any, apiPermissions: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const permissions: Name[] = apiPermissions.map((permission): Name => {
                    return {
                        value: permission['id'],
                        label: permission['trn-name'],
                        optionalColumns: [{ key: 'site', value: permission['site']?.['site-name'] }]
                    }
                })

                //This is specifically for selection field only system owner can assign system owner permission so remove it for others
                if (loggedInUserProfileId !== ROLES.ID_SYSTEM_OWNER && profileId !== ROLES.ID_SYSTEM_OWNER) {
                    const newPermission = permissions.filter(permission => parseInt(permission.value) !== ROLES.ID_SYSTEM_OWNER);
                    pagination.totalCount = newPermission.length;
                    return resolve({ rows: newPermission, pagination });
                }

                return resolve({ rows: permissions, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getPermissions = async (translate: Translator, listParameters?: ListQuery, loggedInUserProfileId?: number): Promise<PermissionWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=site,profileType";
        let roleProfileFields = "fields[roleProfiles]=trnName,profileSysname,";
        roleProfileFields += "totalUsers,profileSysname,profileSysnameType,profileType,site";
        const siteFields = "fields[site]=siteName";
        const url = `roleProfiles?${includes}&${roleProfileFields}&${siteFields}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, apiPermissions: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const permissions: Permission[] = apiPermissions.map((permission): Permission => {
                    return {
                        id: permission['id'],
                        name: permission['trn-name'],
                        type: getPermissionTypes(translate).find((permissionType) => permissionType.id === apiPermissions['profile-sysname'])?.name,
                        logins: permission['total-users'],
                        site: permission['site']['site-name'],
                        siteId: permission['site']['id'],
                        templateKey: permission['profile-sysname'] === "USER_DEFINED" ? 1 : 0,
                        pInheritsFromId: permission["profile-inherits-from"],
                        profileSysType: permission['profile-sysname-type'],
                        profileTypeId: apiPermissions[0]['profile-type']['id'],
                    }
                })

                if (loggedInUserProfileId !== ROLES.ID_SYSTEM_OWNER) {
                    const newPermission = permissions.filter(permission => Number(permission.id) !== ROLES.ID_SYSTEM_OWNER);     //Todo - Investigate type casting
                    pagination.totalCount = newPermission.length;
                    return resolve({ rows: newPermission, pagination });
                }

                return resolve({ rows: permissions, pagination });
            })
        });
    }
    catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getPermissionById = async (itemId: string, translate: Translator): Promise<Permission> => {
    try {
        const roleProfileFields = "fields[roleProfiles]=trnName,profileSysname,profileSysnameType,profileType,site,profileInheritsFrom,pDesc,totalUsers";
        const siteFields = "fields[site]=siteName";
        const includes = `include=site,profileType`;
        const url = `roleProfiles/${itemId}?${includes}&${roleProfileFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, apiPermissions: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                
                const permission: Permission = {
                    id: apiPermissions['id'],
                    name: apiPermissions['trn-name'],
                    type: getPermissionTypes(translate).find((permissionType) => permissionType.id === apiPermissions['profile-sysname'])?.name,
                    logins: apiPermissions['total-users'],
                    site: apiPermissions['site']['site-name'],
                    siteId: apiPermissions['site']['id'],
                    templateKey: apiPermissions['profile-sysname'] === "USER_DEFINED" ? 1 : 0,
                    pInheritsFrom: apiPermissions['profile-inherits-from'],
                    description: apiPermissions['p-desc'],
                    pInheritsFromId: apiPermissions["profile-inherits-from-id"],
                    profileSysType: apiPermissions['profile-sysname-type'],
                    profileTypeId: apiPermissions['profile-type']['id'],
                }

                return resolve(permission);
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const storePermission = async (permission: Permission) => {
    try {
        const createPermission = !!!permission.id;
        const newRecord = {
            data: {
                type: 'profile',
                id: permission.id,
                attributes: {
                    trnName: permission.name,
                    inheritsFromId: permission.pInheritsFromId,
                    pDesc: permission.description,
                    pSysnameType: permission.profileSysType,
                    pHidden: 0,
                },
                relationships: {
                    profileType: {
                        data: {
                            type: "profileType",
                            id: permission.profileTypeId
                        }
                    },
                    site: {
                        data: {
                            type: 'site',
                            id: permission.siteId
                        }
                    }
                }
            }
        }

        const url = `profile${createPermission ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createPermission, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createPermission) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, permission) => {
                        if (error) {
                            return reject({ status: response.status, id: permission.id, success: false, message: genericError });
                        }
                        return resolve({ id: permission.id, success: true });
                    })

                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: permission.id, success: true })
                }
                const content = await response.json();
                return reject({ status: response.status, id: permission.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: permission.id, success: false, message: genericError });

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deletePermission = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `profile/${id}`;
        const permission = {
            data: {
                type: "profile",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, permission)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

